import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `memory` in Brazilian Portuguese. Declared, never inferred, so a gap fails `tsc`.
 *
 * ── Uma preferência é feminina, e isso decide meia dúzia de palavras ──
 *
 * *Preferência* é feminino, então tudo que concorda com ela vem no feminino: "exibidas",
 * "adicionada", "usada", "Sugerida". O inglês não marca nenhuma dessas — "shown", "added",
 * "used" e "Suggested" são invariáveis — e é exatamente por isso que o catálogo inglês
 * declara as duas formas do plural: uma distinção que só existe na outra língua tem que ter
 * onde morar (PRD-27 D-21).
 *
 * ── "Em toda parte" ──
 *
 * *Everywhere* é o nome da partição global, e o inglês evita "Global"/"Platform" de
 * propósito: responde onde a preferência vai aparecer, não qual subsistema é dono dela. "Em
 * toda parte" faz a mesma coisa e é o que alguém diria em voz alta; "Global" seria a
 * tradução literal e a resposta errada.
 *
 * ── "memória", nunca "lembrete" ──
 *
 * `memória` é o termo ratificado (ADR-0018) e vale para o título da página. As entradas
 * individuais continuam sendo *preferências*, como no inglês, porque é o que elas são: o que
 * o assistente guarda sobre você, e não uma anotação que você deixou para si.
 */
export const memory: Messages['memory'] = {
  title: 'Memória',
  lead: 'Preferências que o assistente guarda sobre você. Você pode editar ou remover qualquer uma delas, e a mudança já vale na sua próxima mensagem.',

  whereTitle: 'Onde cada uma vale',
  whereGlobal: 'As preferências que você salva aqui valem em toda parte. Todo agente pode usá-las.',
  whereAgent:
    'As preferências que um agente aprende enquanto você conversa com ele ficam com aquele agente. Nenhum outro agente pode vê-las nem usá-las.',

  everywhere: 'Em toda parte',
  /** O nome do agente é dado e nunca se traduz (D-17). */
  agentOnly: (name) => `Só em ${name}`,

  addCategoryLabel: 'Categoria',
  addStatementLabel: 'O que lembrar',
  addPlaceholder: 'ex.: Sempre me dê a resposta curta primeiro e depois o detalhe.',
  add: 'Adicionar',
  addHint: 'Tudo que você adicionar aqui vale em toda parte.',

  filterLabel: 'Mostrar',
  filterAll: 'Todas as preferências',
  filterGlobal: 'Só em toda parte',
  filterCount: (fmt, label, count) => `${label} (${fmt.formatCount(count)})`,

  listTitle: 'Preferências salvas',
  /**
   * O particípio concorda com *preferências*, e o zero fica no singular em português:
   * "0 exibida", "1 exibida", "2 exibidas". O inglês diz "shown" nas três.
   */
  listCount: (fmt, count) =>
    pluralCategory(fmt.locale, count) === 'one'
      ? `${fmt.formatCount(count)} exibida`
      : `${fmt.formatCount(count)} exibidas`,

  suggested: 'Sugerida — confirme para usar',
  confirm: 'Confirmar',
  confirming: 'Confirmando…',
  confirmHint: 'Confirmar significa que isto é verdade sobre você. Até lá, não é usada em nenhuma resposta.',
  confirmFailed: 'Não foi possível confirmar. Tente de novo.',

  enable: 'Usar esta preferência',
  disable: 'Parar de usar esta preferência',
  forget: 'Esquecer isto',
  /** Sem preposição: `formatAgo` já entrega "há 5 min." e "adicionada em há 5 min." é quebrado. */
  added: (when) => `adicionada ${when}`,
  used: (fmt, count) => `usada ${fmt.formatCount(count)}×`,

  categories: {
    output_preference: 'Como você gosta das respostas',
    domain_context: 'Contexto sobre o seu trabalho',
    working_style: 'Como você gosta de trabalhar'
  },

  empty: {
    title: 'Nada lembrado ainda',
    body: 'Adicione algo acima, ou simplesmente peça a um agente para lembrar disso durante uma conversa.'
  },
  emptyFiltered: {
    title: 'Nada aqui',
    body: 'Nenhuma preferência corresponde a este filtro. Volte para todas as preferências para ver o resto.'
  }
}
